import { chakra, Stack } from '@chakra-ui/react';
import { TextLink } from '@time-line/components/common';
import { useWindowScroll } from 'react-use';
import { scrollToElement } from 'utils/utils';

export interface ContentItem {
  text: string;
  id: string;
}

interface ContentsContentProps {
  contents: ContentItem[];
  hasDisclaimer?: boolean;
  hasReferences?: boolean;
  hasTranscript?: boolean;
  hasKeyTakeaways?: boolean;
  hasContributors?: boolean;
  onItemClick?: () => void;
}

export function ContentsContent({
  contents,
  hasDisclaimer,
  hasReferences,
  hasTranscript,
  hasKeyTakeaways,
  hasContributors,
  onItemClick,
}: ContentsContentProps) {
  const { y } = useWindowScroll();

  const handleItemClick = (id: string) => {
    scrollToElement({ id, scrollYPos: y });

    if (onItemClick) {
      onItemClick();
    }
  };

  const showContents = contents.length > 1 || hasTranscript;

  return (
    <>
      {showContents && (
        <Stack spacing="3xs">
          {contents.map((item) => (
            <TextLink
              as="button"
              key={item.id}
              type="button"
              onClick={() => handleItemClick(item.id)}
              display="block"
              textAlign="left"
              textProps={{
                size: 'body2',
                color: 'blackAlpha.700',
                _hover: {
                  color: 'gray.600',
                },
              }}
            >
              <chakra.p noOfLines={1}>{item.text}</chakra.p>
            </TextLink>
          ))}
          {hasKeyTakeaways && (
            <TextLink
              as="button"
              type="button"
              onClick={() => handleItemClick('key-takeaways')}
              display="block"
              textAlign="left"
              textProps={{
                size: 'body2',
                color: 'blackAlpha.700',
                _hover: {
                  color: 'gray.600',
                },
              }}
            >
              What to know
            </TextLink>
          )}
          {hasTranscript && (
            <TextLink
              as="button"
              type="button"
              onClick={() => handleItemClick('transcript')}
              display="block"
              textAlign="left"
              textProps={{
                size: 'body2',
                color: 'blackAlpha.700',
                _hover: {
                  color: 'gray.600',
                },
              }}
            >
              Transcript
            </TextLink>
          )}
          {hasContributors && (
            <TextLink
              as="button"
              type="button"
              onClick={() => handleItemClick('contributors')}
              display="block"
              textAlign="left"
              textProps={{
                size: 'body2',
                color: 'blackAlpha.700',
                _hover: {
                  color: 'gray.600',
                },
              }}
            >
              Authors
            </TextLink>
          )}
        </Stack>
      )}
      {(hasReferences || hasDisclaimer) && (
        <Stack spacing="3xs" mt="2xs">
          {hasReferences && (
            <TextLink
              type="button"
              as="a"
              href="#references"
              key="contents-references"
              onClick={() => handleItemClick('references')}
              textProps={{
                size: 'detail',
                color: 'blackAlpha.700',
                _hover: {
                  color: 'gray.600',
                },
              }}
            >
              References
            </TextLink>
          )}
          {hasDisclaimer && (
            <TextLink
              as="a"
              href="#disclaimer"
              type="button"
              key="contents-disclaimer"
              onClick={() => handleItemClick('disclaimer')}
              textProps={{
                size: 'detail',
                color: 'blackAlpha.700',
                _hover: {
                  color: 'gray.600',
                },
              }}
            >
              Disclaimer
            </TextLink>
          )}
        </Stack>
      )}
    </>
  );
}
